import axios, { AxiosError, AxiosRequestConfig } from "axios";
import userInfoService from "@/util/userInfoService";
import Vue from "vue";
import { BEARER_PREFIX, clearAuthHeader, hasValidAuthHeader, stripApiPrefix, syncGroupClaims } from "./authShared";

const TOKENS_STORAGE_KEY = "tokens";
const REDIRECT_URL_KEY = "redirect-url";

const getCognitoConfig = () => {
    const cognitoLoginUrl = Vue?.prototype?.$config?.VITE_APP_COGNITO_LOGIN_URL ??
        Vue?.prototype?.$config?.COGNITO_LOGIN_URL;
    const clientId = Vue?.prototype?.$config?.VITE_APP_COGNITO_CLIENT_ID ??
        Vue?.prototype?.$config?.COGNITO_CLIENT_ID;

    if (!cognitoLoginUrl || !clientId) {
        throw new Error('Cognito configuration is missing. Please configure VITE_APP_COGNITO_LOGIN_URL and VITE_APP_COGNITO_CLIENT_ID in appConfig.json');
    }

    // Strip any trailing slash(es) so concatenated auth paths don't become
    // "//login" / "//logout" / "//oauth2/token", which Cognito's API Gateway
    // rejects with "Missing Authentication Token".
    return { cognitoLoginUrl: cognitoLoginUrl.replace(/\/+$/, ""), clientId };
};

const sha256 = async (str: string): Promise<ArrayBuffer> => {
    return await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
};

const generateNonce = async (): Promise<string> => {
    const randomValues = crypto.getRandomValues(new Uint32Array(4));
    const hash = await sha256(randomValues.toString());
    const hashArray = Array.from(new Uint8Array(hash));
    return hashArray.map(byte => byte.toString(16).padStart(2, "0")).join("");
};

const base64URLEncode = (buffer: ArrayBuffer): string => {
    const byteArray = new Uint8Array(buffer);
    const byteString = String.fromCharCode(...byteArray);
    return btoa(byteString)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
};

/** Whether exchanging the returned code will reload the app at the page the sign-in started from. */
export const hasPendingReturnUrl = (): boolean => sessionStorage.getItem(REDIRECT_URL_KEY) !== null;

const redirectToPrevious = (): void => {
    const url = sessionStorage.getItem(REDIRECT_URL_KEY);
    sessionStorage.removeItem(REDIRECT_URL_KEY);
    if (url !== null) window.location.assign(url);
};

/**
 * Sends the browser to the hosted Cognito login. Once the code is exchanged, the app reloads at
 * `returnTo`; null skips that reload and leaves the caller's route in charge of where to go.
 */
export const redirectToLogin = async (returnTo: string | null = window.location.href): Promise<void> => {
    const { cognitoLoginUrl, clientId } = getCognitoConfig();
    const state = await generateNonce();
    const codeVerifier = await generateNonce();
    sessionStorage.setItem(`codeVerifier-${state}`, codeVerifier);
    const codeChallenge = base64URLEncode(await sha256(codeVerifier));
    if (returnTo === null) {
        sessionStorage.removeItem(REDIRECT_URL_KEY);
    } else {
        sessionStorage.setItem(REDIRECT_URL_KEY, returnTo);
    }
    const params = new URLSearchParams({
        response_type: "code",
        client_id: clientId,
        redirect_uri: window.location.origin,
        state,
        code_challenge_method: "S256",
        code_challenge: codeChallenge,
    });
    window.location.assign(`${cognitoLoginUrl}/login?${params.toString()}`);
};

const fetchToken = async (searchParams: URLSearchParams): Promise<any> => {
    const { cognitoLoginUrl, clientId } = getCognitoConfig();
    const state = searchParams.get("state");
    const codeVerifier = sessionStorage.getItem(`codeVerifier-${state}`);
    const res = await fetch(`${cognitoLoginUrl}/oauth2/token`, {
        method: "POST",
        headers: new Headers({ "content-type": "application/x-www-form-urlencoded" }),
        body: new URLSearchParams({
            grant_type: "authorization_code",
            client_id: clientId,
            code: searchParams.get("code") ?? "",
            code_verifier: codeVerifier ?? "",
            redirect_uri: window.location.origin,
        }).toString(),
    });
    const tokens = await res.json();
    if (!res?.ok) {
        throw new Error(tokens?.error_description ?? tokens?.error ?? "Token exchange failed");
    }
    localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(tokens));
    axios.defaults.headers.Authorization = `${BEARER_PREFIX}${tokens.id_token}`;
    sessionStorage.removeItem(`codeVerifier-${state}`);

    syncGroupClaims();
    redirectToPrevious();
    return tokens;
};

const isTokenExpiringSoon = (idToken: string, bufferMinutes: number = 5): boolean => {
    try {
        // Decode JWT payload (second segment) to read its expiry.
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const expirationTime = payload.exp * 1000;
        const bufferTime = bufferMinutes * 60 * 1000;
        return Date.now() > (expirationTime - bufferTime);
    } catch {
        // If we can't decode it, treat it as expiring.
        return true;
    }
};

const performTokenRefresh = async (): Promise<any> => {
    const { cognitoLoginUrl, clientId } = getCognitoConfig();
    const storedToken = localStorage.getItem(TOKENS_STORAGE_KEY);

    if (!storedToken) {
        throw new Error("No tokens to refresh");
    }

    const tokens = JSON.parse(storedToken);
    const refreshToken = tokens.refresh_token;

    if (!refreshToken) {
        throw new Error("No refresh token available");
    }

    const res = await fetch(`${cognitoLoginUrl}/oauth2/token`, {
        method: "POST",
        headers: new Headers({ "content-type": "application/x-www-form-urlencoded" }),
        body: new URLSearchParams({
            grant_type: "refresh_token",
            client_id: clientId,
            refresh_token: refreshToken,
        }).toString(),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Token refresh failed");
    }

    const newTokens = await res.json();
    // Cognito doesn't always return a new refresh token; preserve the existing one.
    if (!newTokens.refresh_token) {
        newTokens.refresh_token = refreshToken;
    }
    localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(newTokens));
    axios.defaults.headers.Authorization = `${BEARER_PREFIX}${newTokens.id_token}`;

    // Keep server-side group claims in sync after a silent refresh, matching the
    // initial auth-code exchange in fetchToken().
    syncGroupClaims();

    return newTokens;
};

// Coalesce concurrent refreshes onto a single in-flight request, mirroring the
// single-flight pattern in microsoftAuth.ts. Without this, a burst of requests
// hitting a near-expired token (on startup, or several 401s landing at once)
// would each fire their own refresh round-trip to Cognito.
let inFlightRefresh: Promise<any> | null = null;

const refreshTokens = async (): Promise<any> => {
    if (inFlightRefresh) return inFlightRefresh;

    inFlightRefresh = performTokenRefresh();
    try {
        return await inFlightRefresh;
    } finally {
        inFlightRefresh = null;
    }
};

// Cognito accepts a code once, but several callers see it in the URL at startup: the root route's
// guard, the dashboard worker's token request, and request interceptors. They all share the one
// exchange, and callers after it settles get its result rather than spending the code again.
let codeExchange: { code: string; tokens: Promise<any> } | null = null;

const exchangeCode = (searchParams: URLSearchParams): Promise<any> => {
    const code = searchParams.get("code") ?? "";
    if (codeExchange?.code !== code) {
        codeExchange = { code, tokens: fetchToken(searchParams) };
    }
    return codeExchange.tokens;
};

// After a sign-in fails, requests that find no session fail instead of starting another one. The
// failure page then stays up rather than looping through a hosted login that fails again; signing
// in again is left to the user (the root route sends them to the hosted login directly).
let signInFailed = false;

export const markSignInFailed = (): void => {
    signInFailed = true;
};

const signInFromRequest = async (): Promise<void> => {
    if (!signInFailed) await redirectToLogin();
};

/** Whether tokens are stored. They may have expired; requests refresh them or end in the login redirect. */
export const hasStoredSession = (): boolean => localStorage.getItem(TOKENS_STORAGE_KEY) !== null;

export const getToken = async (forceRefresh: boolean = false): Promise<string | undefined> => {
    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.get("code")) {
        const tokens = await exchangeCode(searchParams);
        return tokens.id_token;
    }

    const storedToken = localStorage.getItem(TOKENS_STORAGE_KEY);

    if (!storedToken) {
        return;
    }

    const tokens = JSON.parse(storedToken);

    if (forceRefresh || isTokenExpiringSoon(tokens.id_token)) {
        try {
            const newTokens = await refreshTokens();
            return newTokens.id_token;
        } catch (err) {
            console.warn("Token refresh failed, returning existing token", err);
            // The existing token may still be valid, so fall back to it.
            return tokens.id_token;
        }
    }

    return tokens.id_token;
};

// Marks a request that has already had one silent-refresh retry, so a repeat 401
// falls through to the login redirect instead of looping.
interface RetryableRequestConfig extends AxiosRequestConfig {
    _cognitoRetry?: boolean;
}

const errorHandler = async (err: AxiosError): Promise<any> => {
    const config = err?.response?.config as RetryableRequestConfig | undefined;

    if (err?.response?.status === 401 && config && !config._cognitoRetry) {
        config._cognitoRetry = true;
        try {
            // Reactively recover from an expired id_token: refresh, then replay the
            // original request with the new token (mirrors microsoftAuth.ts).
            const newTokens = await refreshTokens();
            if (!config.headers) config.headers = {};
            config.headers.Authorization = `${BEARER_PREFIX}${newTokens.id_token}`;
            return axios(config);
        } catch (refreshError) {
            // The refresh token is gone or rejected — only now wipe tokens and send
            // the user to the login page.
            console.warn("Silent token refresh after 401 failed; redirecting to login:", refreshError);
            localStorage.removeItem(TOKENS_STORAGE_KEY);
            await signInFromRequest();
            return Promise.reject(err);
        }
    }

    return Promise.reject(err);
};

const requestHandler = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    config.url = stripApiPrefix(config.url);

    try {
        await userInfoService.fetchUserInfoIfNeeded(config.url);
    } catch (error) {
        // Don't fail the request - let it proceed and handle auth errors later.
        console.warn("Failed to fetch user info in request handler:", error);
    }

    if (hasValidAuthHeader(config?.headers?.Authorization)) return config;

    // getToken() proactively refreshes a token that is expired or within the expiry
    // buffer, handles the ?code exchange, and returns nothing when there is no
    // session — so a stale id_token is never attached to the outgoing request.
    const token = await getToken();

    if (!token) {
        await signInFromRequest();
        return config;
    }

    axios.defaults.headers.Authorization = `${BEARER_PREFIX}${token}`;
    if (!config.headers) config.headers = {};
    config.headers.Authorization = `${BEARER_PREFIX}${token}`;

    return config;
};

export function setupCognito(): void {
    axios.interceptors.request.use(requestHandler);
    axios.interceptors.response.use(
        response => response,
        (err: AxiosError) => errorHandler(err)
    );
}

export function logoutCognito(): void {
    const { cognitoLoginUrl, clientId } = getCognitoConfig();
    localStorage.removeItem(TOKENS_STORAGE_KEY);
    clearAuthHeader();
    const params = new URLSearchParams({
        response_type: "code",
        client_id: clientId,
        redirect_uri: window.location.origin,
    });
    window.location.assign(`${cognitoLoginUrl}/logout?${params.toString()}`);
}
